import * as React from "react";
import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import { m } from "framer-motion";
import {
  SurfaceProvider,
  useElevatedSurfaceClasses,
} from "~/lib/surface-context";
import {
  fluidScaleFadeMotion,
  fluidSprings,
  omitMotionConflictingProps,
  useFluidExitUnmount,
  useFluidOverlayRoot,
} from "~/components/ui/fluid-motion";
import { cn } from "~/lib/utils";

type TooltipMotionContextValue = {
  actionsRef: React.RefObject<TooltipPrimitive.Root.Actions | null>;
  open: boolean;
};

const TooltipMotionContext =
  React.createContext<TooltipMotionContextValue | null>(null);

function useTooltipMotionContext(): TooltipMotionContextValue {
  const context = React.useContext(TooltipMotionContext);

  if (context) {
    return context;
  }

  return {
    actionsRef: { current: null },
    open: false,
  };
}

interface TooltipProps extends TooltipPrimitive.Root.Props {
  align?: TooltipPrimitive.Positioner.Props["align"];
  className?: string;
  content?: React.ReactNode;
  delayDuration?: number;
  side?: TooltipPrimitive.Positioner.Props["side"];
  sideOffset?: TooltipPrimitive.Positioner.Props["sideOffset"];
}

function Tooltip({
  align,
  children,
  className,
  content,
  delayDuration,
  side,
  sideOffset,
  actionsRef,
  defaultOpen = false,
  onOpenChange,
  open: controlledOpen,
  ...props
}: TooltipProps) {
  const overlayRoot = useFluidOverlayRoot<
    TooltipPrimitive.Root.Actions,
    TooltipPrimitive.Root.ChangeEventDetails
  >({
    actionsRef,
    defaultOpen,
    onOpenChange,
    open: controlledOpen,
  });

  const contextValue = React.useMemo<TooltipMotionContextValue>(
    () => ({
      actionsRef: overlayRoot.actionsRef,
      open: overlayRoot.open,
    }),
    [overlayRoot.actionsRef, overlayRoot.open],
  );

  if (content === undefined) {
    return (
      <TooltipMotionContext.Provider value={contextValue}>
        <TooltipPrimitive.Root
          {...overlayRoot.rootProps}
          data-slot="tooltip"
          {...props}
        >
          {children}
        </TooltipPrimitive.Root>
      </TooltipMotionContext.Provider>
    );
  }

  if (!React.isValidElement(children)) {
    throw new Error("Tooltip with content requires a single React element child.");
  }

  return (
    <TooltipMotionContext.Provider value={contextValue}>
      <TooltipPrimitive.Root
        {...overlayRoot.rootProps}
        data-slot="tooltip"
        {...props}
      >
        <TooltipTrigger delay={delayDuration} render={children} />
        <TooltipContent
          align={align}
          className={className}
          side={side}
          sideOffset={sideOffset}
        >
          {content}
        </TooltipContent>
      </TooltipPrimitive.Root>
    </TooltipMotionContext.Provider>
  );
}

function TooltipTrigger({ ...props }: TooltipPrimitive.Trigger.Props) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  align = "center",
  className,
  side = "inline-end",
  sideOffset = 8,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<TooltipPrimitive.Positioner.Props, "align" | "side" | "sideOffset">) {
  const { actionsRef, open } = useTooltipMotionContext();
  const surface = useElevatedSurfaceClasses(2, 3);

  useFluidExitUnmount(open, fluidSprings.fast, () => {
    actionsRef.current?.unmount();
  });

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        className="isolate z-50 outline-none"
        side={side}
        sideOffset={sideOffset}
      >
        <SurfaceProvider value={surface.level}>
          <TooltipPrimitive.Popup
            data-slot="tooltip-content"
            render={(popupProps, state) => (
              <m.div
                {...omitMotionConflictingProps(popupProps)}
                {...fluidScaleFadeMotion(state.open, fluidSprings.fast)}
                onAnimationComplete={() => {
                  if (!state.open) {
                    actionsRef.current?.unmount();
                  }
                }}
              />
            )}
            className={cn(
              surface.className,
              "z-50 max-w-64 origin-(--transform-origin) rounded-xl px-3 py-2 text-meta font-medium text-popover-foreground outline-none",
              className,
            )}
            {...props}
          />
        </SurfaceProvider>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipContent, TooltipTrigger };
export type { TooltipProps };
